import { formati } from "../formati.ts";

const f = formati("it");

type Servizi = { servizi: readonly string[] };

/** Il verdetto sul rilascio senza interruzione e le fasi di un'operazione. */
export const rilascio = {
  "rilascio.requisito.strategia.titolo": "Lo stack usa la strategia blue/green",
  "rilascio.requisito.strategia.come": "Si cambia da qui: gli stack nascono con la ricreazione.",
  "rilascio.requisito.proxy.titolo": "Portman raggiunge Nginx Proxy Manager",
  "rilascio.requisito.proxy.assente":
    "Un amministratore può installarlo da qui con le impostazioni consigliate, oppure collegarne " +
    "uno esistente con PORTMAN_NPM_URL, PORTMAN_NPM_IDENTITY e PORTMAN_NPM_SECRET, che richiede un riavvio.",
  "rilascio.requisito.proxy.ok": "Risponde all'indirizzo configurato.",
  "rilascio.requisito.proxy.muto": "Il proxy configurato non ha risposto.",
  "rilascio.requisito.instradamento.titolo": "Almeno un servizio ha un dominio associato",
  "rilascio.requisito.instradamento.come": "Si aggiunge in Rilascio › Instradamenti: è il dominio a cui spostare il traffico.",
  "rilascio.requisito.porte.titolo": "Nessun servizio pubblica porte host",
  "rilascio.requisito.porte.ok":
    "Sotto blue/green l'indirizzo di un servizio è il suo dominio, e gli altri servizi lo raggiungono per nome.",
  "rilascio.requisito.porte.mancante": (p: Servizi) =>
    `Due generazioni vive insieme non possono bindare la stessa porta. Togli "ports:" da ${f.elenco(p.servizi)}: ` +
    "il loro indirizzo è il dominio, e gli altri servizi li raggiungono per nome.",
  "rilascio.requisito.nomi.titolo": "Nessun servizio impone un nome fisso al container",
  "rilascio.requisito.nomi.ok": "I container li nomina il progetto, così due generazioni convivono senza saperlo.",
  "rilascio.requisito.nomi.mancante": (p: Servizi) =>
    `Un nome fissato con "container_name:" è globale sul demone Docker, quindi due generazioni non ` +
    `possono averlo insieme: la seconda non nascerebbe. Toglilo da ${f.elenco(p.servizi)} — ` +
    "sotto blue/green i container li nomina il progetto, e il proxy li raggiunge lo stesso.",
  "rilascio.requisito.fissi-dominio.titolo": "Nessun servizio fisso ha un dominio",
  "rilascio.requisito.fissi-dominio.ok": "Il traffico si sposta fra generazioni: un fisso non ne ha.",
  "rilascio.requisito.fissi-dominio.mancante": (p: Servizi) =>
    `Il traffico non avrebbe dove spostarsi. Togli il dominio da ${f.elenco(p.servizi)}, ` +
    "oppure togli x-portman.fisso se deve essere instradato.",
  "rilascio.requisito.fissi-dipendenze.titolo": "Nessun servizio fisso dipende da uno non fisso",
  "rilascio.requisito.fissi-dipendenze.ok":
    "I fissi nascono prima della generazione, e --no-deps ignorerebbe la dipendenza.",
  "rilascio.requisito.fissi-dipendenze.mancante": (p: Servizi) =>
    `${f.elenco(p.servizi)} nasce prima della generazione: non può dipendere da un servizio ` +
    "che ancora non esiste. Togli la dipendenza, o marca fisso anche il servizio da cui dipende.",
  "rilascio.requisito.reti-nome.titolo": "Nessuna applicazione usa una rete con nome fisso",
  "rilascio.requisito.reti-nome.ok":
    "Una rete con name: non esterna unirebbe le due generazioni sullo stesso DNS.",
  "rilascio.requisito.reti-nome.mancante": (p: Servizi) =>
    `La rete con name: mette ${f.elenco(p.servizi)} sulla stessa rete in entrambe le generazioni: ` +
    "il DNS di Docker alternerebbe vecchio e nuovo. Togli name: oppure dichiara la rete external.",
  "rilascio.requisito.fissi-dati.titolo": "Nessun servizio fisso ha ancora i dati in una generazione",
  "rilascio.requisito.fissi-dati.ok":
    "Un servizio appena marcato fisso nascerebbe con un volume vuoto, lontano dai suoi dati.",
  "rilascio.requisito.fissi-dati.mancante": (p: Servizi & { progetto: string }) =>
    `${f.elenco(p.servizi)} è marcato fisso, ma i suoi dati stanno ancora in "${p.progetto}": ` +
    "il fisso nascerebbe con un volume vuoto. Ferma il servizio nella generazione e copia il volume " +
    "come descrive la guida (PROVA-LOCALE.md, «Un servizio che diventa fisso»), oppure togli x-portman.fisso.",

  "rilascio.mancanza.strategia": "lo stack non usa il blue/green",
  "rilascio.mancanza.proxy": "manca il collegamento a Nginx Proxy Manager",
  "rilascio.mancanza.instradamento": "nessun servizio ha un dominio",
  "rilascio.mancanza.porte": "ci sono porte host pubblicate",
  "rilascio.mancanza.nomi": "ci sono nomi di container fissati",
  "rilascio.mancanza.fissi-dominio": "un servizio fisso ha un dominio",
  "rilascio.mancanza.fissi-dipendenze": "un servizio fisso dipende da uno non fisso",
  "rilascio.mancanza.reti-nome": "una rete con nome fisso unirebbe le generazioni",
  "rilascio.mancanza.fissi-dati": "un servizio fisso ha ancora i dati in una generazione",

  "rilascio.verdetto.attivo": "Attivo per questo stack",
  "rilascio.verdetto.unaMancante": (p: { cosa: string }) => `Non attivo: ${p.cosa}`,
  "rilascio.verdetto.mancano": (p: { n: number; totale: number }) =>
    `Non attivo: mancano ${p.n} condizioni su ${p.totale}`,
  "rilascio.verdetto.nonConfigurabile": "Non attivo: il rilascio senza interruzione non è configurabile",
  "rilascio.effetto.senzaInterruzione":
    "Al prossimo avvio la versione nuova parte accanto a quella attiva e il traffico si sposta solo quando è pronta: senza interruzione.",
  "rilascio.effetto.ricreazione":
    "Al prossimo avvio i container verranno ricreati: il servizio resta irraggiungibile per circa un secondo.",

  "rilascio.avvertimento.healthcheck.titolo": (p: Servizi) =>
    `${f.elenco(p.servizi)} ${p.servizi.length === 1 ? "non dichiara" : "non dichiarano"} un healthcheck nel compose`,
  "rilascio.avvertimento.healthcheck.dettaglio":
    "Se non lo dichiara nemmeno l'immagine, il traffico si sposta appena il processo parte, " +
    "che non significa pronto a servire. Il rilascio lo dirà nei log, dove il dato è quello del container reale.",
  "rilascio.avvertimento.volumi.titolo": (p: Servizi) =>
    `${f.elenco(p.servizi)} ${p.servizi.length === 1 ? "monta" : "montano"} un volume che cambia a ogni generazione`,
  "rilascio.avvertimento.volumi.dettaglio":
    "A ogni generazione Docker crea un volume diverso. Se deve sopravvivere ai rilasci, il servizio va marcato fisso; se è una cache, va bene così.",
  "rilascio.avvertimento.ex-fissi.titolo": (p: Servizi) =>
    `${f.elenco(p.servizi)} non ${p.servizi.length === 1 ? "è più fisso" : "sono più fissi"}`,
  "rilascio.avvertimento.ex-fissi.dettaglio":
    "Al rilascio il container del progetto fisso viene rimosso, ma il suo volume resta dov'è: " +
    "la generazione nuova parte con un volume vuoto.",

  "rilascio.fase.verifica": "Verifica della configurazione",
  "rilascio.fase.immagini": "Scaricamento delle immagini",
  "rilascio.fase.avvio": "Avvio dei servizi",
  "rilascio.fase.avvioGenerazione": "Avvio della nuova generazione",
  "rilascio.fase.salute": "Attesa che la nuova generazione sia pronta",
  "rilascio.fase.traffico": "Spostamento del traffico",
  "rilascio.fase.rimozione": "Rimozione della versione precedente",
};
